/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, ReactNode, useCallback, useState } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { createGeneralCall } from '@altermaind/core/requests-status-management';
import { Box, Sizes, Typography, useThemeValue } from '@altermaind/web/ui';
import { useNotification } from '@/_shared/hooks/useNotification';
import { Paginator, usePaginatedTable } from '../Table';
import { PaginatedCardsSkeleton } from './PaginatedCardsSkeleton';

// Type for the call prop, similar to PaginatedTable
type CreateGeneralCallMustHaveFullRequestSelector = ReturnType<
  typeof createGeneralCall<any, any, any, { [key: string]: any; fullRequest: any }>
>;

export type PaginatedCardsProps = {
  call: CreateGeneralCallMustHaveFullRequestSelector;
  pageSize?: number;
  elementsForRow?: number;
  renderCard: (item: any, index: number) => ReactNode;
  cardGap?: keyof Sizes;
  onCardHover?: (item: any) => void;
  paginedProps: ReturnType<typeof usePaginatedTable>;
  loading?: boolean;
  skeleton?: ReactNode;
  'data-testid'?: string;
  'card-item-testid'?: string;
  'no-data-testid'?: string;
};

const DEFAULT_PAGE_SIZE = 21;

/**
 * A React functional component that renders a paginated list of cards. It supports custom rendering of cards, pagination controls, and loading states.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.call - Contains the hook for fetching data and related configurations.
 * @param {number} [props.pageSize=DEFAULT_PAGE_SIZE] - The number of items to display per page.
 * @param {Function} props.renderCard - A function to render each card. Receives the item and its index as arguments.
 * @param {Sizes} [props.cardGap] - The gap size between cards.
 * @param {Object} props.paginedProps - Contains pagination-related properties and methods.
 * @param {boolean} [props.loading=false] - Indicates whether the component is in a loading state.
 * @param {React.Component} props.skeleton - A skeleton component to display while loading.
 * @param {Function} [props.onCardHover] - A callback function triggered when a card is hovered over. Receives the hovered item as an argument.
 * @param {string} [props['data-testid']='paginated-cards'] - A test ID for the main container.
 * @param {string} [props['no-data-testid']='no-data-in-paginated-cards'] - A test ID for the "no data" message.
 *
 * @returns {JSX.Element} The rendered paginated cards component.
 *
 * @example
 *
 * const {
 *   data,
 *   totalItems,
 *   execute,
 * } = call.hook();
 *
 * const paginedProps = usePaginatedTable({
 *   rowData: data,
 *   pageSize: PAGE_SIZE,
 *   totalItems,
 * });
 *
 * <PaginatedCards
 *   call={{...call}}
 *   pageSize={10}
 *   renderCard={(item, idx) => <Card key={idx} data={item} />}
 *   paginedProps={paginedProps}
 *   loading={false}
 *   skeleton={<Skeleton />}
 *   onCardHover={(item) => console.log('Hovered item:', item)}
 * />
 */
export const PaginatedCards: FC<PaginatedCardsProps> = ({
  call,
  pageSize = DEFAULT_PAGE_SIZE,
  elementsForRow = 3,
  renderCard,
  cardGap = 'xl',
  paginedProps,
  loading = false,
  skeleton,
  onCardHover,
  'data-testid': dataTestId = 'paginated-cards',
  'card-item-testid': cardItemTestId = 'paginated-card-item',
  'no-data-testid': noDataTestId = 'no-data-in-paginated-cards',
}) => {
  const t = useTranslations();
  const { notify } = useNotification();
  const { execute: executeCall, data: rowData, totalItems, fullRequest } = call.hook();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [internalStart, setInternalStart] = useState(0);

  const {
    paginatedData,
    currentPageNumber,
    goToFirst,
    goToLast,
    lastPageNumber,
    goToPage,
    next,
    previous,
  } = paginedProps;

  // Fetch data logic (like PaginatedTable)
  const fetchData = useCallback(
    ({ start = 0 }) => {
      const params: Record<string, unknown> = {
        ...fullRequest?.payload,
        start,
        length: pageSize,
      };
      executeCall({
        ...params,
        errorSaga: () => {
          notify(t('errors.genericApiError'), 'error');
        },
      });
    },
    [pageSize, executeCall, notify, t, fullRequest],
  );

  const addData = useCallback(
    (pageNumber: number) => {
      if (currentPageNumber === lastPageNumber && rowData?.length > 0) {
        if (totalItems <= rowData?.length) {
          return;
        }
        setInternalStart(pageSize * (pageNumber - 1));
        fetchData({ start: pageSize * (pageNumber - 1) });
      }
      if (pageNumber && currentPageNumber !== pageNumber && rowData?.length) {
        if (totalItems <= rowData?.length) {
          return;
        }
        setInternalStart(pageSize * (pageNumber - 1));
        fetchData({ start: pageSize * (pageNumber - 1) });
      }
    },
    [pageSize, currentPageNumber, fetchData, lastPageNumber, rowData?.length, totalItems],
  );

  const internalNext = useCallback(() => {
    addData(currentPageNumber + 1);
    next();
  }, [addData, next, currentPageNumber]);

  const internalPrevious = useCallback(() => {
    addData(currentPageNumber - 1);
    previous();
  }, [addData, previous, currentPageNumber]);

  const internalGoToLast = useCallback(() => {
    addData(lastPageNumber);
    goToLast();
  }, [addData, goToLast, lastPageNumber]);

  const internalGoToFirst = useCallback(() => {
    const firstPage = 1;
    addData(firstPage);
    goToFirst();
  }, [addData, goToFirst]);

  const internalGoToPage = useCallback(
    (pageNumber: number) => {
      addData(pageNumber);
      goToPage(pageNumber);
    },
    [addData, goToPage],
  );

  const gapSpace = useThemeValue('space', cardGap);

  if (loading) {
    return <PaginatedCardsSkeleton Skeleton={skeleton} pageSize={elementsForRow || 3} />;
  }

  return (
    <Box data-testid={dataTestId}>
      <Box
        style={{ gap: gapSpace }}
        display='grid'
        gridTemplateColumns={`repeat(${elementsForRow}, 1fr)`}
      >
        {paginatedData.map((item, idx) => (
          <Box
            key={item.id ?? item.paymentId ?? idx}
            width='100%'
            flex={1}
            onMouseEnter={() => onCardHover?.(item)}
            data-testid={`${cardItemTestId}-${idx}`}
          >
            {renderCard(item, idx)}
          </Box>
        ))}
      </Box>
      {paginatedData.length === 0 && (
        <Typography
          data-testid={noDataTestId}
          textAlign='center'
          variant='p2/light'
          mt={'250px' as never}
          mb={'250px' as never}
        >
          {t('table.noContent')}
        </Typography>
      )}

      <Paginator
        goToFirst={internalGoToFirst}
        currentPageNumber={currentPageNumber}
        goToLast={internalGoToLast}
        goToPage={internalGoToPage}
        lastPageNumber={lastPageNumber}
        next={internalNext}
        previous={internalPrevious}
        disableNext={currentPageNumber >= lastPageNumber}
        disablePrevious={currentPageNumber <= 1}
      />
    </Box>
  );
};
